import { ASMDisplayState } from '@rome/response/common/enums/ASMDisplayState';
import ApiCaller from 'actions/ApiCaller';
import baseAjaxConfig from 'actions/BaseAjaxConfig';
import get from 'lodash/get';
import { objectToQueryParams } from 'utils/urlutils';
import { asmProductOOS } from 'actions/saleActions';
import { checkoutStateAdvanceAction } from './checkoutVisualStateActions';
import { stringTemplate } from 'components/StringWidget/StringWidget';
export const CHECKOUT_SUMMARY_REQUEST = 'CHECKOUT_SUMMARY_REQUEST';
export const CHECKOUT_SUMMARY_SUCCESS = 'CHECKOUT_SUMMARY_SUCCESS';
export const CHECKOUT_SUMMARY_ERROR = 'CHECKOUT_SUMMARY_ERROR';
export const CHECKOUT_SUMMARY_CLEAR = 'CHECKOUT_SUMMARY_CLEAR';
export const CHECKOUT_CLEAR_ACTION_DETAILS = 'CHECKOUT_CLEAR_ACTION_DETAILS';
export function checkoutSummaryRequestAction() {
    return { type: CHECKOUT_SUMMARY_REQUEST };
}
export function checkoutSummarySuccessAction(data) {
    return { type: CHECKOUT_SUMMARY_SUCCESS, data };
}
export function checkoutSummaryErrorAction(error) {
    return { type: CHECKOUT_SUMMARY_ERROR, error };
}
// Before ML-based AIS validations, we used to advance the "EXPANDED" option
// to order summary if there was only one address. However, after those
// changes, backend would return validation messages which need to be checked,
// and only in the normal case should we be automatically moving user to order
// summary.
const shouldMoveFromAddressToOrderSummary = response => {
    const billingAddressInfos = get(response, 'addressData.billingAddressInfos', []);
    const selectedAddressId = get(response, 'addressData.selectedAddressId');
    const selectedAddress = billingAddressInfos.find(address => address.id === selectedAddressId);
    const validationStatus = get(selectedAddress, 'addressValidation.status');
    if (billingAddressInfos.length === 1) {
        if (validationStatus === 'BAD') {
            return false;
        }
        return true;
    }
    return false;
};
export function fetchCheckoutSummaryAction(isLoginFlow = false, view) {
    return (dispatch, getState) => {
        dispatch(checkoutSummaryRequestAction());
        const buyNow = get(getState(), ['checkoutPage', 'buyNow']) || {};
        const saalListingsData = get(getState(), 'saalListingsData');
        const { eids, domain = '', customPayload } = buyNow;
        let body = { checkoutType: domain.toUpperCase() };
        if (eids) {
            // TODO: From where do we get the quantity?
            body = {
                cartRequest: {
                    cartContext: {
                        [eids]: {
                            quantity: buyNow.quantity
                        }
                    }
                },
                checkoutType: domain.toUpperCase()
            };
            if (saalListingsData) {
                const saalListings = get(saalListingsData, 'listings', []);
                saalListings.map(listing => {
                    body['cartRequest']['cartContext'][listing] = {
                        parentContext: eids
                    };
                });
            }
            if (customPayload) {
                // Append customPayload which is of cartContext type.
                // Use customPayload only when you want to add additional payload along with the above default payload
                Object.assign(body['cartRequest']['cartContext'], customPayload);
            }
        }
        else if (domain === 'egv' && buyNow.egvData) {
            buyNow.egvData.egvRequests = buyNow.egvData.egvRequests.map(egv => {
                return {
                    amount: decodeURIComponent(egv.amount),
                    senderName: decodeURIComponent(egv.senderName),
                    receiverEmailId: decodeURIComponent(egv.receiverEmailId),
                    receiverName: decodeURIComponent(egv.receiverName),
                    noOfVouchers: decodeURIComponent(egv.noOfVouchers),
                    message: decodeURIComponent(egv.message)
                };
            });
            body = buyNow.egvData;
        }
        const queryParams = objectToQueryParams({
            loginFlow: isLoginFlow && !eids,
            view
        });
        const settings = Object.assign({}, baseAjaxConfig, {
            pathname: `/api/5/checkout${queryParams}`,
            action: CHECKOUT_SUMMARY_REQUEST,
            acceptErrors: true
        });
        return ApiCaller.post(settings, body).then(json => {
            const response = get(json, ['RESPONSE'], {});
            const asmDisplayState = get(response, [
                'asmData',
                'renderableComponents',
                0,
                'value',
                'callout',
                'displayState'
            ]);
            if (asmDisplayState === ASMDisplayState.OOS) {
                dispatch(asmProductOOS(get(response, ['asmData', 'renderableComponents', 0])));
            }
            // Opens order summary section if there is only one address
            if (shouldMoveFromAddressToOrderSummary(response)) {
                dispatch(checkoutStateAdvanceAction());
            }
            // TODO: handle empty response, perhaps
            return dispatch(checkoutSummarySuccessAction(response));
        }, err => {
            dispatch(checkoutSummaryErrorAction({
                ERROR_MESSAGE: err.ERROR_MESSAGE || stringTemplate('buyNowDegradeMessage'),
                ERROR_CODE: err.ERROR_CODE
            }));
            return Promise.reject(err);
        });
    };
}
/**
 * Action for GET checkout summary (Mostly to be used in retry checkout scenarios.
 * @param error
 * @returns {*|Promise.<T>|Thenable<U>|Promise<U>}
 */
export function getCheckoutSummaryAction(query) {
    if (query.token) {
        return getCheckoutResumeAction(query.token);
    }
    const settings = Object.assign({}, baseAjaxConfig, {
        pathname: '/api/5/checkout',
        action: CHECKOUT_SUMMARY_REQUEST,
        acceptErrors: true,
        query: {
            v: new Date().getTime(),
            error: query.error
        }
    });
    return (dispatch) => {
        return ApiCaller.get(settings).then(json => {
            const response = get(json, ['RESPONSE'], {});
            // TODO: handle empty response, perhaps
            return dispatch(checkoutSummarySuccessAction(response));
        }, err => {
            dispatch(checkoutSummaryErrorAction(err));
            return Promise.reject(err);
        });
    };
}
/**
 * Action for GET checkout summary (Mostly to be used in resume checkout scenarios.
 * @param error
 * @returns {*|Promise.<T>|Thenable<U>|Promise<U>}
 */
export function getCheckoutResumeAction(token) {
    const settings = Object.assign({}, baseAjaxConfig, {
        pathname: '/api/5/checkout/resume',
        action: CHECKOUT_SUMMARY_REQUEST,
        acceptErrors: true
    });
    const body = {
        token
    };
    return (dispatch) => {
        return ApiCaller.post(settings, body).then(json => {
            const response = get(json, ['RESPONSE'], {});
            return dispatch(checkoutSummarySuccessAction(response));
        }, err => {
            dispatch(checkoutSummaryErrorAction(err));
            return Promise.reject(err);
        });
    };
}
export function clearCheckoutActionDetails() {
    return { type: CHECKOUT_CLEAR_ACTION_DETAILS };
}
export function clearCheckoutSummaryDetails(isLoggedIn) {
    return { type: CHECKOUT_SUMMARY_CLEAR, isLoggedIn };
}
